/**
 * What every guest page wears around its content, as the confirmed prototype draws it: the business's mark and
 * name, the quiet "Made with Flashroom", and the page's own ground in the business's colours.
 */
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { WatchPageResponse } from "../../../shared/types";
import { BrandGlyph } from "../../components/BrandMark";
import { GIcon, type GIconName } from "./GIcon";

/** The brand words as the prototype sets them: the first two words strong, the rest quiet. */
function BrandWords({ name }: { name: string }): ReactNode {
  const words = name.trim().split(/\s+/);
  if (words.length <= 2) return <b>{name}</b>;
  return (
    <>
      <b>{words.slice(0, 2).join(" ")}</b> <span>{words.slice(2).join(" ")}</span>
    </>
  );
}

export function Brand({ brand, href }: { brand: WatchPageResponse["brand"]; href?: string }): ReactNode {
  return (
    <a className="brand" href={href ?? "#top"} aria-label={brand.workspace_name}>
      <span className="brand-m">
        {brand.logo_url ? <img src={brand.logo_url} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : brand.crest}
      </span>
      <span className="brand-w">
        <BrandWords name={brand.workspace_name} />
      </span>
    </a>
  );
}

export function Made(): ReactNode {
  return (
    <a className="made" href="https://flashsuite.ai/flashroom/" target="_blank" rel="noopener">
      <BrandGlyph size={16} />
      Made with Flashroom
    </a>
  );
}

/** The guest look on the page's own ground, so an overscroll never shows the host app's colour. */
export function useGuestGround(vars: Record<string, string>): void {
  useEffect(() => {
    const html = document.documentElement;
    const was = html.style.background;
    html.style.background = vars["--night"] ?? "";
    return () => {
      html.style.background = was;
    };
  }, [vars]);
}

/** How long a toast stays. */
export const TOAST_MS = 3200;

/** One short line at the top that goes by itself, as the prototype's toast. */
export function useToast(): { toast: { words: string; icon: GIconName } | null; say: (words: string, icon?: GIconName) => void } {
  const [toast, setToast] = useState<{ words: string; icon: GIconName } | null>(null);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const say = useCallback((words: string, icon: GIconName = "check") => {
    window.clearTimeout(timer.current);
    setToast({ words, icon });
    timer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  return { toast, say };
}

export function Toast({ toast }: { toast: { words: string; icon: GIconName } | null }): ReactNode {
  return (
    <div className={toast ? "toast on" : "toast"} role="status" aria-live="polite">
      {toast ? (
        <>
          <GIcon name={toast.icon} size={18} />
          <span>{toast.words}</span>
        </>
      ) : null}
    </div>
  );
}

/**
 * A sheet for small extras: it rises from the bottom on a phone and slides in as a slim drawer on a laptop. The
 * scrim behind it closes it, and so does Escape.
 */
export function Sheet({ label, tall, onClose, children }: { label: string; tall?: boolean; onClose: () => void; children: ReactNode }): ReactNode {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setOn(true));
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", escape);
    return () => {
      window.cancelAnimationFrame(id);
      document.removeEventListener("keydown", escape);
    };
  }, [onClose]);
  return (
    <>
      <div className={on ? "scrim on" : "scrim"} role="presentation" onClick={onClose} />
      <div className={["sheetx", tall ? "tall" : "", on ? "on" : ""].filter(Boolean).join(" ")} role="dialog" aria-label={label}>
        <span className="grab" />
        {children}
      </div>
    </>
  );
}
